import React from 'react';
import {
  CARDTYPE,
  IWorkoutCardProps,
  isLargeCard,
  isSmallCard,
  hasRating,
  completedDate,
  determineFallbackImg,
} from '@ifit/mp-card-utilities';
import cx from 'classnames';
import { withTranslation, useTranslation } from '@ifit/rapport-webview';
import NativeClick, { METHODS } from '@ifit/mp-native-click';
import Card from '@ifit/mp-base-card';
import LineWrap from '@ifit/mp-line-wrap';
import Estimates from '@ifit/mp-estimates';
import ProgressiveBackground from '@ifit/mp-progressive-background';
import ProgressBar from '@ifit/mp-progress-bar';
import Intensity from '@ifit/mp-intensity';
import Chip from '@ifit/mp-chip';
import './index.scss';
import rapportPackage from './rapport-web.json';

export const WorkoutCard = (props: IWorkoutCardProps) => {
  const {
    cardSize,
    data,
    locked,
    overlayText,
    programInfo,
    disableNative,
    verbose,
    horizontal,
    category,
    recommendationLabel,
    forceFallbackImg,
    useMetricUnits,
    equipmentType,
    themeClass,
    lineWrapBasedOn,
  } = props;
  const { locale, _translate } = useTranslation();
  let { fallbackImage } = props;

  if (!fallbackImage) {
    const { workoutType } = data;
    fallbackImage = determineFallbackImg(workoutType, CARDTYPE.WORKOUT);
  }

  const { image, rating, estimates, intensity, title, lastCompleted } = data;
  const small = isSmallCard(cardSize);
  const large = isLargeCard(cardSize);
  const displayRating = hasRating(rating);
  const completed = completedDate(lastCompleted);

  const showCategory =
    category && (horizontal || (small && category.length <= 10) || !small);

  return (
    <Card
      className={cx('card-template', 'workout-card', {
        large,
        small,
        horizontal,
        themeClass,
      })}
    >
      <>
        <div className="thumbnail-container">
          <ProgressiveBackground
            image={!forceFallbackImg ? image : undefined}
            fallbackImage={fallbackImage}
            lazy={{ load: false }}
            resizeOptions={{ position: '' }}
          />
          {completed && !programInfo && !(locked && small) && !recommendationLabel && (
            <div className="completed-chip">
              <Chip>
                <span>
                  {small
                    ? _translate('COMPLETED', {
                        context:
                          'Signifies that a workout has been completed to user',
                      })
                    : _translate('COMPLETED:', {
                        context:
                          'Signifies that a workout has been completed to user. We will show the date of completion after the ":".',
                      })}{' '}
                </span>
                {!small && completed}
              </Chip>
            </div>
          )}
          {!small && !completed && overlayText && !recommendationLabel && (
            <div className="chip-overlay">
              <Chip>{overlayText}</Chip>
            </div>
          )}
          {!horizontal && (!completed || (completed && programInfo)) && !overlayText && !(small && locked) && !recommendationLabel && (
            <div className="intensity-chip">
              <Chip>
                <Intensity intensity={intensity} />
              </Chip>
            </div>
          )}
          {locked && !recommendationLabel && (
            <div className="locked-chip">
              <Chip>
                <Card.Locked />
              </Chip>
            </div>
          )}
          {recommendationLabel && (
            <div className="recommendation-chip">
              <Chip recommended locked={locked}>
                {recommendationLabel}
              </Chip>
            </div>
          )}
        </div>
        <div className="details">
          <LineWrap
            className="title"
            maxLines={2}
            text={title}
            basedOn={lineWrapBasedOn}
          />
          <Estimates
            estimates={estimates}
            useMetricUnits={useMetricUnits}
            equipmentType={equipmentType}
          />
          {data && programInfo ? (
            <NativeClick
              disableNative={disableNative}
              verbose={verbose}
              objectToSend={METHODS.programSelected(programInfo.seriesId || programInfo.challengeId)}
            >
              <Card.ContinueSeriesInfo
                programInfo={programInfo}
                locale={locale}
                locked={locked}
              />
            </NativeClick>
          ) : (
            <Card.Footer>
              {displayRating && (
                <Card.Rating rating={rating} size={0.65} unitSize={'em'} />
              )}
              <div className="category">{showCategory && category}</div>
            </Card.Footer>
          )}
        </div>
        {completed && !programInfo && <ProgressBar progress={100} />}
      </>
    </Card>
  );
};

export default withTranslation(rapportPackage)(WorkoutCard);
